<template>
  <div v-if="!gateway || gateway === 'check' || gateway === 'wire'"></div>
  <credit-card
    ref="cc"
    :key="`${gateway}-${recurring}-${currency}`"
    :gateway="gateway"
    v-bind="creditCardBooleanAttributes"
    :currency="currency"
    v-else-if="!gateway.toLowerCase().startsWith('cardknox')"
  >
    Oops! It seems our credit card widget failed to load. Please contact our web support team at webhelp@ou.org for assistance. We apologize for the inconvenience and appreciate your patience.
  </credit-card>
  <div v-else key="cardknox">
    <nav>
      <label :class="{ active: cardknoxmode == 'cc' }"
        ><input type="radio" value="cc" v-model="cardknoxmode" />Credit Card</label
      >
      <label :class="{ active: cardknoxmode == 'ach' }"
        ><input type="radio" value="ach" v-model="cardknoxmode" />ACH</label
      >
    </nav>
    <main>
      <template v-if="cardknoxmode == 'cc'">
        <iframe
          data-ifields-id="card-number"
          key="cc"
          data-ifields-placeholder="Card Number"
          :src="`https://cdn.cardknox.com/ifields/${cardknoxVersion}/ifield.htm`"
          style="border: none; height: 44px; overflow: hidden; width: 100%"
        ></iframe>
        <FormRow style="margin-top: 10px">
          <div>
            <select name="expiration_month" v-model="xExpMonth">
              <option :value="null">Month</option>
              <option value="1">01</option>
              <option value="2">02</option>
              <option value="3">03</option>
              <option value="4">04</option>
              <option value="5">05</option>
              <option value="6">06</option>
              <option value="7">07</option>
              <option value="8">08</option>
              <option value="9">09</option>
              <option value="10">10</option>
              <option value="11">11</option>
              <option value="12">12</option>
            </select>
          </div>
          <div>
            <select name="expiration_year" v-model="xExpYear">
              <option :value="null">Year</option>
              <option v-for="i in 7">{{ i + new Date().getFullYear() - 1 }}</option>
            </select>
          </div>
          <div>
            <iframe
              data-ifields-id="cvv"
              data-ifields-placeholder="CVV"
              :src="`https://cdn.cardknox.com/ifields/${cardknoxVersion}/ifield.htm`"
              style="border: none; height: 45px; overflow: hidden; width: 85px"
            ></iframe>
            <input name="xCVV" type="hidden" data-ifields-id="cvv-token" ref="xCVV" />
            <input
              name="xCardNum"
              type="hidden"
              data-ifields-id="card-number-token"
              ref="xCardNum"
            />
          </div>
        </FormRow>
      </template>
      <template v-else>
        <FormRow>
          <FormCol>
            <iframe
              key="ach"
              data-ifields-id="ach"
              data-ifields-placeholder="Checking Account Number"
              :src="`https://cdn.cardknox.com/ifields/${cardknoxVersion}/ifield.htm`"
              style="border: none; height: 43px; overflow: hidden; width: 100%"
            ></iframe>
            <input data-ifields-id="ach-token" ref="xACH" type="hidden" />
          </FormCol>
          <FormCol>
            <input type="text" v-model="routingNumber" placeholder="Routing Number" />
          </FormCol>
        </FormRow>
      </template>
    </main>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { card, status } from '~/shared/status';
import { shouldCancelSubmitOnPopupClosed } from '~/shared/popup-submit-guard';
import { usePaymentForm } from '~/shared/use-payment-form';
import { resolveCreditCardBooleanAttributes } from '~/shared/runtime-environment'
import {
  attachCreditCardEventListeners,
  detachCreditCardEventListeners,
} from '~/shared/credit-card-events'

  window.dataLayer = window.dataLayer || []

export default defineComponent({
  props: {
    lang: {
      type: String,
    },
    check_text: {
      type: String,
      default: "Write checks out to XXXXX",
    },
  },
  setup() {
    // Type declaration for credit-card custom element with exposed methods
    interface CreditCardElement extends HTMLElement {
      getToken: (amount: number) => Promise<any>
      getPaymentMethod: () => string | null
      resetCard: () => void
    }

    const { form, setValue, settings, testMode, setCcElement } = usePaymentForm()
    const cc = ref()
    const noCVV = computed(() => !!settings?.removeCVV)
    const currency = computed(() => form.value.currency)
    const recurring = computed(() => !!form.value.dayOfMonth || !!form.value.periodMonths)
    const creditCardBooleanAttributes = computed(() => resolveCreditCardBooleanAttributes({
      testMode,
      noCVV: noCVV.value,
      recurring: recurring.value
    }))

    // Handle status updates from credit card widget via CustomEvent
    function handleStatusEvent(event: CustomEvent) {
      console.log('[CustomEvent] handleStatusEvent called, event.detail:', event.detail);
      status.value = event.detail;
      console.log('[CustomEvent] status.value is now:', status.value);
    }

    // Handle payment method selection via CustomEvent
    function handlePaymentMethodEvent(event: CustomEvent) {
      const method = event.detail;
      console.log('[CustomEvent] Payment method selected:', method);
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'Payment Selected',
          paymentType: method
        })
      } catch (e) {
        // Swallow errors to avoid impacting UX
      }
    }

    // Handle popup closed via CustomEvent
    function handlePopupClosedEvent() {
      console.log('[CustomEvent] Popup closed');
      const paymentMethod = cc.value?.getPaymentMethod?.() || cc.value?.$el?.getPaymentMethod?.();

      // Popup wallets close their window as part of a successful flow, so
      // closing alone is not a cancellation signal for those methods.
      if ((window as any).formService && shouldCancelSubmitOnPopupClosed(paymentMethod)) {
        (window as any).formService.send({ type: 'CANCEL_SUBMIT' });
      }
    }

    let attachedCcElement: CreditCardElement | null = null;

    const creditCardEventListeners = {
      handleStatusEvent,
      handlePaymentMethodEvent,
      handlePopupClosedEvent,
    }

    const usesCreditCardElement = (gateway: string | null | undefined) =>
      !!gateway &&
      gateway !== 'check' &&
      gateway !== 'wire' &&
      !gateway.toLowerCase().startsWith('cardknox')

    // Function to attach the event listeners
    const attachListener = (retryCount = 0) => {
      // Try to get the actual DOM element from the ref
      const ccElement = (cc.value?.$el || cc.value) as CreditCardElement | null;
      console.log('[CustomEvent] Attempt', retryCount, '- Got element:', ccElement, 'tagName:', ccElement?.tagName);

      if (ccElement && ccElement.tagName === 'CREDIT-CARD') {
        console.log('[CustomEvent] ✅ Adding event listeners for cc:status, cc:payment-method, cc:popup-closed');
        attachedCcElement = attachCreditCardEventListeners(
          attachedCcElement,
          ccElement,
          creditCardEventListeners
        ) as CreditCardElement;
        // Store element reference for API calls
        setCcElement(ccElement);
      } else if (retryCount < 20) {
        // Keep retrying for up to 1 second (20 * 50ms)
        console.log('[CustomEvent] Element not ready, retrying in 50ms...');
        setTimeout(() => attachListener(retryCount + 1), 50);
      } else {
        console.warn('[CustomEvent] ❌ Could not find credit-card element after', retryCount, 'retries');
      }
    };

    // Track if getToken method has been found
    let getTokenAttached = false;

    const resetCreditCardElementReference = () => {
      detachCreditCardEventListeners(attachedCcElement, creditCardEventListeners)
      attachedCcElement = null
      setCcElement(null)
      getTokenAttached = false
    }

    // Function to poll for getToken method availability
    const waitForGetToken = (retryCount = 0) => {
      if (getTokenAttached) return;

      const ccElement = (cc.value?.$el || cc.value) as CreditCardElement | null;
      console.log('[CC Setup] Checking for getToken, attempt', retryCount, '- element:', ccElement, 'has getToken:', typeof ccElement?.getToken);

      if (ccElement && typeof ccElement.getToken === 'function') {
        console.log('[CC Setup] ✅ getToken method found! Calling setCcElement');
        setCcElement(ccElement);
        getTokenAttached = true;
      } else if (retryCount < 40) {
        // Keep retrying for up to 2 seconds (40 * 50ms)
        setTimeout(() => waitForGetToken(retryCount + 1), 50);
      } else {
        console.error('[CC Setup] ❌ getToken method never became available after', retryCount, 'retries');
      }
    };

    const setupCreditCardElement = () => {
      nextTick(() => {
        waitForGetToken();
        attachListener();
      });
    };

    // Set up event listeners when component mounts
    onMounted(() => {
      console.log('[CC Setup] onMounted - gateway:', form.value.gateway, 'cc.value:', cc.value);
      setupCreditCardElement();
    })

    // Reattach when any key input remounts the credit-card custom element.
    watch([() => form.value.gateway, recurring, currency], ([newGateway, isRecurring, newCurrency], oldIdentity) => {
      console.log('[CC Setup] Credit-card identity watch fired - gateway:', newGateway, 'recurring:', isRecurring, 'currency:', newCurrency, 'attachedCcElement:', attachedCcElement, 'getTokenAttached:', getTokenAttached);

      resetCreditCardElementReference();

      if (oldIdentity) {
        status.value = { id: 'idle' };
      }

      if (usesCreditCardElement(newGateway)) {
        setupCreditCardElement();
      }
    }, { immediate: true })

    // Clean up event listeners when component unmounts
    onBeforeUnmount(() => {
      detachCreditCardEventListeners(attachedCcElement, creditCardEventListeners)
    })
    
    watch(status,() => {
      if (form.value.gateway?.toLowerCase().includes('cardcom')) {
        setValue('toc', status.value.id == 'valid')
        }
      }, {
      immediate: true
    }) 

    watch(() => form.value.gateway, async () => {
      if (form.value.gateway?.toLowerCase().includes('cardcom')) {
        setValue('toc', status.value.id == 'valid')
      }

      if (form.value.gateway?.toLowerCase().startsWith('cardknox')) {
        const key = await fetch(`https://payment.ouapis.org/api/payment/cardknoxKey?gateway=${form.value.gateway}&IsDevelopment=${testMode ? 'true' : 'false'}`).then(x=>x.text())
        await loadScript();
        // @ts-ignore
        setAccount(
          key,
          "paymentapi",
          "1.0.2"
        );
        await nextTick()

        const numStyle = {
          width: "96%",
          height: "42px",
          display: "block",
          border: "1px solid #ddd",
          padding: "0 10px",
          borderRadius: "4px",
          background: "#f7f9fb",
          fontSize: "15px",
          color: "#222",
        };

        const cvvStyle = {
          width: "60px",
          height: "40px",
          display: "block",
          border: "1px solid #ddd",
          padding: "0 10px",
          borderRadius: "4px",
          background: "#f7f9fb",
          fontSize: "15px",
          color: "#222",
          marginTop: "3px",
        };

        const achStyle = {
          width: "265px",
          height: "38px",
          display: "block",
          border: "1px solid #ddd",
          padding: "0 10px",
          borderRadius: "4px",
          background: "#f7f9fb",
          fontSize: "15px",
          color: "#222",
          marginTop: "3px",
        };

        //@ts-ignore
        setIfieldStyle("card-number", numStyle);
        //@ts-ignore
        setIfieldStyle("cvv", cvvStyle);
        //@ts-ignore
        setIfieldStyle("ach", achStyle);
        //@ts-ignore
        addIfieldKeyPressCallback(function (data) {
          if (data.lastIfieldChanged === "cvv") {
            cvvValid.value = data.cvvIsValid && !data.cvvIsEmpty;
          } else if (data.lastIfieldChanged === "card-number") {
            cardValid.value = data.cardNumberIsValid;
            if (data.issuer === "amex") {
              cvvValid.value = data.cvvLength === 4;
            } else {
              cvvValid.value = data.cvvLength === 3;
            }
          } else if (data.lastIfieldChanged === "ach") {
            achValid.value = data.achIsValid;
          }
        });

        watch(
        [xExpMonth, xExpYear, cardValid, cvvValid, achValid, routingNumber],
        () => {
          if (
            xExpMonth.value &&
            xExpYear.value &&
            cvvValid.value &&
            cardValid.value
          ) {
            //@ts-ignore
            getTokens(() => {
              card.value = {
                number: xCardNum.value.value,
                month: xExpMonth.value,
                year: xExpYear.value,
                cvc: xCVV.value.value,
              }

              status.value = ({id: 'valid'})
            });
          } else if (cardknoxmode.value == "ach" && achValid.value) {
            //@ts-ignore
            getTokens(() => {
              card.value = {
                achToken: xACH.value.value,
                routingNumber: routingNumber.value,
              };

              status.value = ({id: 'valid'})
            })
          } else {
            card.value = null

            status.value = ({id: 'invalid', error: ''})
          }
        }
      );
      }

      }, {
      immediate: true
    })

    let cardknoxLoaded = false;
    const cardknoxVersion = "2.13.2204.1401";
    const xExpMonth = ref();
    const xExpYear = ref();
    const xCVV = ref();
    const xCardNum = ref();
    const xACH = ref();
    const cvvValid = ref(false);
    const cardValid = ref(false);
    const cardknoxmode = ref("cc");
    const routingNumber = ref();
    const achValid = ref();

    function loadScript() {
      return new Promise<void>((res) => {
        if (cardknoxLoaded) return res();
        const script = document.createElement("script");
        script.src = `https://cdn.cardknox.com/ifields/${cardknoxVersion}/ifields.min.js`;
        script.onload = () => {
          cardknoxLoaded = true;
          return res();
        };
        document.body.appendChild(script);
      });
    }

    return {
      gateway: computed(() => form.value.gateway!),
      cc,
      status,
      cardknoxVersion,
      xExpMonth,
      xExpYear,
      xCVV,
      xCardNum,
      cardValid,
      cvvValid,
      cardknoxmode,
      xACH,
      routingNumber,
      //@ts-ignore
      testMode,
      creditCardBooleanAttributes,
      noCVV,
      currency,
      recurring
    }
  }
})
</script>
<style scoped lang="scss">
.cc-field {
  background: #f7f9fb;
  border-radius: 4px;
  font-size: 0;
  width: 300px;
}
.flex {
  align-items: center;
  display: flex;

  input {
    margin-right: 7px;
    margin-bottom: 5px;
  }
}
.check-text {
  text-align: center;
}
.form-group div {
  margin-right: 10px;
  flex: 1;
}
nav {
  overflow: hidden;
  margin-bottom: -1px;
  > label {
    overflow: hidden;
    padding: 16px 15px;
    display: inline-block;
    border: 1px solid #ddd;
    margin-bottom: -3px !important;
    border-radius: 4px 4px 0 0;
    background: #f5f4f4;
    cursor: pointer;
    font-size: 14px;
    color: #666;
    &.active {
      background: white;
      color: #222;
      border-bottom: 1px solid white;
    }
  }
  input {
    position: absolute;
    left: -20px;
    z-index: -1;
  }
}
main {
  padding-top: 1rem;
  border-top: 1px solid #ddd;
}
</style>
